import type { IBinder } from '../interface/binder';
import type { IContainer } from '../interface/container';
import util from '../private';

export function isBinder(object: unknown): object is IBinder<unknown> {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IBinder<unknown>> = object as Partial<IBinder<unknown>>;

    return (
        !isNullOrUndefined(record.id) &&
        !isNullOrUndefined(record.dependencies) &&
        !isNullOrUndefined(record.bind) &&
        !isNullOrUndefined(record.remove) &&
        !isNullOrUndefined(record.expose) &&
        !isNullOrUndefined(record.inject) &&
        !isNullOrUndefined(record.build)
    );
}

export function buildBindable<T>(
    args: Readonly<{
        id: Readonly<symbol>;
        dependencies?: symbol[];
        bind: (value: T, container: IContainer) => void;
        remove: (container: IContainer) => void;
        inject: (container: IContainer) => T;
        build: (container: IContainer) => Promise<T>;
    }>,
): Readonly<IBinder<T>> {
    const binder: IBinder<T> = {
        id: args.id,
        dependencies: args.dependencies ?? [],
        bind: async container => {
            /* Make sure we don't recreate it, if it already exists */
            if (container.exists(args.id)) {
                util.log.warn(`Attempted to re-build ${args.id.toString()} (already in container)`);
            } else {
                const builderResult = await args.build(container);
                args.bind(builderResult, container);
            }
        },
        remove: container => {
            args.remove(container);
        },
        expose: (srcContainer: IContainer, dstContainer: IContainer) => {
            const existingModule = args.inject(srcContainer);
            args.bind(existingModule, dstContainer);
        },
        inject: async container => {
            const value: T | undefined = args.inject(container);

            if (value !== undefined) {
                return value;
            }

            const build = await args.build(container);

            args.bind(build, container);

            return build;
        },
        build: args.build,
    };

    return Object.freeze(binder);
}
